/**
 * OverlayShowcase — antd 오버레이 컴포넌트(Modal/Drawer/Popover/Tooltip 등) 카탈로그.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import { useState } from 'react'
import {
  Card,
  Button,
  Modal,
  Drawer,
  Popover,
  Tooltip,
  Space,
  Input,
  Form,
  Popconfirm,
  ConfigProvider,
  DatePicker,
  Typography
} from 'antd'
import enUS from 'antd/locale/en_US'
import koKR from 'antd/locale/ko_KR'
import {
  ExportOutlined,
  EditOutlined,
  ExpandOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { useMessage } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

/**
 * ============================================================================
 * OverlayShowcase — 오버레이/팝업 카탈로그
 * ============================================================================
 *
 * 【역할】 사용자 흐름을 잠시 가로채는 오버레이 컴포넌트 군 시연.
 *         + ConfigProvider 로케일 스왑(koKR ↔ enUS) 디모.
 *
 * 【시연 항목】
 *   - Dialog (Modal)        — 기본 / 폼 포함 / 큰(60vw) 다이얼로그
 *   - Modal.confirm         — useModal() imperative confirm/popup
 *   - Drawer                — 좌/우 placement 슬라이드
 *   - Popconfirm            — 인라인 삭제/발행 확인
 *   - Popover/Tooltip       — top/bottom/left/right 4 placement
 *   - ConfigProvider        — locale 동적 스왑 → DatePicker 즉시 반영
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/overlay' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd Modal/Drawer/Popover/Tooltip/Popconfirm/ConfigProvider/Form/Input,
 *           framework useMessage / useLabel
 * 【참고】 https://ant.design/components/modal
 */
const { Title } = Typography

/** 모든 카드 공통 body 스타일 — padding 10, 카드 채움 + 내부 스크롤. */
// 【왜 module-scope?】 매 렌더 새 객체 생성 방지 → Card 의 styles prop reference 안정화
//   antd Card 가 styles 변화를 internal effect 로 감지하면 불필요한 재계산 발생
// 【antd v5 styles 객체】
//   v4 의 bodyStyle 은 v5 에서 styles.body 로 이동
//   header / actions / cover 등 카드 영역별 개별 스타일 지정 가능
//   { body: ..., header: ..., actions: ... }
// 【overflow:auto vs hidden】
//   auto: 콘텐츠가 넘치면 스크롤바 자동 표시 (컴포넌트 카탈로그처럼 가변 콘텐츠 적합)
//   hidden: 잘림 (정확한 레이아웃 강제 시)
const cardBody = { body: { padding: 10, height: '100%', overflow: 'auto' } }

// ─────────────────────────────────────────────────────────
// 컴포넌트 본체
// ─────────────────────────────────────────────────────────
// 7개 모달/드로어/컨펌 오버레이 + 1개 ConfigProvider locale 토글 = 5개 카드 셀.
// 각 오버레이는 독립 boolean state 로 제어 — 동시 열림 가능성 있음 (현 데모는 명시적으로 분리).
//
// 【렌더 순서 주의】
//   contextHolder 는 PageContainer 안에 한 번 렌더해야 modal.confirm 호출이 화면에 표시됨
//   여러 페이지에서 useModal 을 쓰는 경우 각 페이지마다 contextHolder 필요 (전역 1회 X)
/**
 * OverlayShowcase — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function OverlayShowcase() {
  const msg = useMessage()
  const { t } = useLabel()
  // ─── 각 모달/드로어의 open 상태 ─
  // 【왜 useState 7개 vs 단일 객체?】
  //   개별 boolean 이 setter 도 분리되어 의도가 명확 + 한 모달 여닫음이 다른 상태 변경 trigger 없음
  //   객체 하나로 묶으면 setOpen({...prev, basic:true}) 패턴이 필요해 verbose
  const [basicDialog, setBasicDialog] = useState(false)
  const [formDialog, setFormDialog] = useState(false)
  const [maxDialog, setMaxDialog] = useState(false)
  const [leftDrawer, setLeftDrawer] = useState(false)
  const [rightDrawer, setRightDrawer] = useState(false)
  // ConfigProvider locale 토글 (ko ↔ en) — DatePicker 의 i18n 즉시 반영 시연
  const [cpLocale, setCpLocale] = useState<'ko' | 'en'>('ko')
  // ─── Modal.useModal — imperative 호출 + contextHolder 패턴 ─
  // 【왜 Modal.confirm 직접 호출 대신 useModal?】
  //   - Modal.confirm() 정적 호출은 React Context (theme/i18n) 를 못 받음 → 다크모드/언어 깨짐
  //   - useModal 은 contextHolder 를 트리에 박아 Context 상속 → 일관된 스타일
  // 【주의】 contextHolder 를 JSX 에 렌더하지 않으면 모달이 화면에 나타나지 않음
  const [modal, contextHolder] = Modal.useModal()

  /** "ConfirmDialog" 버튼 — 삭제 확인 모달 (danger ok 버튼). */
  const fn_confirmDialog = () => {
    // ─── imperative 삭제 확인 모달 (Modal.useModal) ─
    // ok=danger 빨강 버튼 + onOk/onCancel 토스트 분기
    //
    // 【왜 imperative (modal.confirm) vs declarative (<Modal open>)?】
    //   - imperative: 한 줄로 호출 가능, 단발성 확인에 최적 — JSX 분리 불필요
    //   - declarative: 폼/리스트 등 복잡한 본문, 사용자 입력 수집에 적합
    // 【주의】 onOk 가 Promise 반환 시 자동으로 loading 표시 + reject 시 모달 유지 (편리한 패턴)
    modal.confirm({
      title: t('TTL_DIALOG_CONFIRM_DELETE', '삭제 확인'),
      content: t('MSG_CONFIRM_DELETE', '이 항목을 삭제하시겠습니까?'),
      okText: t('BTN_DELETE', '삭제'),
      cancelText: t('BTN_CANCEL', '취소'),
      okButtonProps: { danger: true },
      onOk: () => msg.byId('MSG_DELETED'),
      onCancel: () => msg.byId('MSG_CANCELED')
    })
  }

  /** "ConfirmPopup" 버튼 — 일반 진행 확인 모달. */
  const fn_confirmPopup = () => {
    // ─── imperative 진행 확인 모달 (일반 색상) ─
    modal.confirm({
      title: t('TTL_CONFIRM', '확인'),
      content: t('MSG_CONFIRM_PROCEED', '이 작업을 진행하시겠습니까?'),
      okText: t('BTN_CONFIRM', '예'),
      cancelText: t('BTN_CANCEL', '아니오'),
      onOk: () => msg.byId('MSG_SAVE_SUCCESS')
    })
  }

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      {contextHolder}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flex: '0 0 auto' }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_OVERLAY_COMPONENTS', 'Overlay Components')}
        </Title>
      </div>

      {/* ─── Grid Areas 레이아웃 ─
          row1: Dialog(3col) + Confirm(3col) — 가장 자주 쓰는 두 패턴 강조
          row2: Drawer(2) + Popconfirm(2) + Tooltip/CP(2)
          【왜 named areas?】 단순 column span 보다 의도(블록명)가 코드에 드러남
                             gridTemplateColumns/Rows 만 바꿔 비율 조정 가능 (영역 코드 수정 X) */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "dialog dialog dialog confirm confirm confirm"
            "drawer drawer popconfirm popconfirm tip tip"
          `,
          gap: 10
        }}
      >
        {/* ─── Dialog(Modal) 카드 — 3종 변형 ─
            기본 / 폼 포함 / 큰(60vw) 다이얼로그
            【언제 width=60vw?】
              - 480px 기본: 단순 입력 1~2 필드
              - 60vw: 데이터 테이블/이미지 갤러리 등 가로 공간 필요
              - 100vw - 32 (전체화면): 고급 편집기 (Excel/PDF 미리보기)
            【주의】 footer={null} 은 기본 OK/Cancel 버튼 제거 — onCancel 만 작동, onOk 안 발생
              사용자 정의 footer 가 있을 때 또는 정보성 모달일 때 사용 */}
        <Card size="small" title="Dialog (Modal)" styles={cardBody} style={{ gridArea: 'dialog' }}>
          <Space wrap>
            <Button type="primary" icon={<ExportOutlined />} onClick={() => setBasicDialog(true)}>
              {t('TTL_DIALOG_DEFAULT', '기본 다이얼로그')}
            </Button>
            <Button icon={<EditOutlined />} onClick={() => setFormDialog(true)}>
              {t('TTL_DIALOG_FORM', '폼 다이얼로그')}
            </Button>
            <Button icon={<ExpandOutlined />} onClick={() => setMaxDialog(true)}>
              {t('TTL_DIALOG_LARGE', '큰 다이얼로그')}
            </Button>
          </Space>

          {/* 기본 다이얼로그 — open prop 으로 controlled
              onOk/onCancel 모두 같은 setter 호출 — 폼 검증 없는 단순 알림 */}
          <Modal
            title={t('TTL_DIALOG_DEFAULT', '기본 다이얼로그')}
            open={basicDialog}
            onOk={() => setBasicDialog(false)}
            onCancel={() => setBasicDialog(false)}
            okText={t('BTN_CONFIRM', '확인')}
            cancelText={t('BTN_CANCEL', '취소')}
            width={480}
          >
            <p>{t('LBL_DEMO_DIALOG_BODY', '기본 다이얼로그 내용입니다.')}</p>
          </Modal>

          {/* 폼 다이얼로그 — Form 컴포넌트를 본문에 포함
              【주의】 실제로는 onOk 에서 form.validateFields() 호출 후 성공 시만 닫아야 함
                     이 데모는 검증 생략 (단순 시연) */}
          <Modal
            title={t('TTL_DIALOG_FORM', '폼 다이얼로그')}
            open={formDialog}
            onOk={() => setFormDialog(false)}
            onCancel={() => setFormDialog(false)}
            okText={t('BTN_SAVE', '저장')}
            cancelText={t('BTN_CANCEL', '취소')}
            width={480}
          >
            <Form layout="vertical">
              <Form.Item label={t('LBL_NAME', '이름')}>
                <Input placeholder={t('LBL_NAME', '이름')} />
              </Form.Item>
              <Form.Item label={t('COL_EMAIL', '이메일')}>
                <Input placeholder={t('COL_EMAIL', '이메일')} />
              </Form.Item>
            </Form>
          </Modal>

          {/* 큰 다이얼로그 — width='60vw' 로 화면 60% 차지
              footer={null} → 기본 OK/Cancel 버튼 제거 (정보성 모달, 사용자가 X 또는 ESC 로 닫음) */}
          <Modal
            title={t('TTL_DIALOG_LARGE', '큰 다이얼로그')}
            open={maxDialog}
            onCancel={() => setMaxDialog(false)}
            footer={null}
            width="60vw"
          >
            <p>{t('LBL_DEMO_DIALOG_LARGE_BODY', '이 다이얼로그는 넓게 표시됩니다.')}</p>
            <p>{t('LBL_DEMO_DIALOG_LARGE_HINT', '상세 정보 화면 등에 활용할 수 있습니다.')}</p>
          </Modal>
        </Card>

        <Card size="small" title="Modal.confirm" styles={cardBody} style={{ gridArea: 'confirm' }}>
          <Space wrap>
            <Button danger icon={<DeleteOutlined />} onClick={fn_confirmDialog}>
              ConfirmDialog
            </Button>
            <Button icon={<QuestionCircleOutlined />} onClick={fn_confirmPopup}>
              ConfirmPopup
            </Button>
          </Space>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 8, fontSize: 12 }}
          >
            Modal.useModal() imperative confirm
          </Typography.Text>
        </Card>

        {/* ─── Drawer 카드 ─
            【언제 Drawer vs Modal?】
              - Modal: 짧은 입력/확인 (3~5 필드) — 화면 중앙, 차단성 높음
              - Drawer: 길고 컨텍스트가 필요한 상세/필터 패널 — 옆에서 슬라이드, 본문이 살짝 보임
            【주의】 placement='left' 와 'right' 는 동시에 열 수 있음 (좌/우 별 state 분리)
                  trap-focus 동작은 antd 가 처리하지만 onClose 콜백 누락 시 ESC 키 안 먹음 */}
        <Card size="small" title="Drawer" styles={cardBody} style={{ gridArea: 'drawer' }}>
          <Space wrap>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => setLeftDrawer(true)}
            >
              {t('LBL_LEFT', '왼쪽')}
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setRightDrawer(true)}>
              {t('LBL_RIGHT', '오른쪽')}
            </Button>
          </Space>
          {/* 좌측 Drawer — placement='left' → 왼쪽에서 슬라이드 인
              주로 네비게이션/메뉴 패턴에 사용 */}
          <Drawer
            title={t('TTL_DRAWER_LEFT', '왼쪽 Drawer')}
            placement="left"
            open={leftDrawer}
            onClose={() => setLeftDrawer(false)}
          >
            <p>{t('LBL_DEMO_DRAWER_LEFT_BODY', '왼쪽에서 슬라이드되는 Drawer입니다.')}</p>
          </Drawer>
          {/* 우측 Drawer — placement='right' → 오른쪽에서 슬라이드 인
              주로 디테일/필터 패턴에 사용 */}
          <Drawer
            title={t('TTL_DRAWER_RIGHT', '오른쪽 Drawer')}
            placement="right"
            open={rightDrawer}
            onClose={() => setRightDrawer(false)}
          >
            <p>{t('LBL_DEMO_DRAWER_RIGHT_BODY', '오른쪽에서 슬라이드되는 Drawer입니다.')}</p>
          </Drawer>
        </Card>

        {/* ─── Popconfirm 카드 ─
            【언제 Popconfirm vs Modal.confirm?】
              - Popconfirm: 액션 버튼 바로 옆 표시, 반복 작업(행 단위 삭제) 흐름 끊지 않음
              - Modal.confirm: 화면 중앙, 위험도 높은 단일 액션 (대량 삭제/시스템 설정)
            【주의】 onConfirm/onCancel 모두 async 가능 — 서버 응답 대기 동안 자동 닫히지 않게
                  okButtonProps={{loading}} 로 조절 가능 */}
        <Card size="small" title="Popconfirm" styles={cardBody} style={{ gridArea: 'popconfirm' }}>
          <Space wrap>
            {/* Popconfirm — 인라인 삭제 확인
                title: 짧은 헤더 / description: 추가 설명 (선택)
                okButtonProps.danger=true → 빨간 OK 버튼 (위험 액션 시각화) */}
            <Popconfirm
              title={t('TTL_DIALOG_CONFIRM_DELETE', '삭제 확인')}
              description={t('MSG_CONFIRM_DELETE', '이 항목을 삭제하시겠습니까?')}
              okText={t('BTN_DELETE', '삭제')}
              cancelText={t('BTN_CANCEL', '취소')}
              okButtonProps={{ danger: true }}
              onConfirm={() => msg.byId('MSG_DELETED')}
              onCancel={() => msg.byId('MSG_CANCELED')}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t('BTN_DELETE_INLINE', '인라인 삭제')}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('LBL_DEMO_PUBLISH', '발행')}
              description={t('MSG_CONFIRM_PUBLISH', '이 글을 발행하시겠습니까?')}
              onConfirm={() => msg.byId('MSG_SAVE_SUCCESS')}
            >
              <Button type="primary">{t('LBL_DEMO_PUBLISH', '발행')}</Button>
            </Popconfirm>
          </Space>
        </Card>

        {/* ─── Popover / Tooltip / ConfigProvider 카드 ─
            【Popover vs Tooltip】
              - Tooltip: 간단한 텍스트 힌트 (hover trigger 기본), 상호작용 불가
              - Popover: 제목+본문+상호작용(버튼/폼) 가능, trigger='click' 권장
            【ConfigProvider locale 패턴】
              - 자식 컴포넌트(특히 DatePicker/Calendar) 에 즉시 i18n 전파
              - 앱 전역 locale 변경 시에는 main.tsx 의 최상위 ConfigProvider 갱신
              - 부분 영역만 다른 locale 이 필요할 때(다국어 UI 디자이너 등) ConfigProvider 중첩 가능 */}
        <Card
          size="small"
          title="Popover / Tooltip / ConfigProvider"
          styles={cardBody}
          style={{ gridArea: 'tip' }}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space wrap size={6}>
              {/* Popover — title + content 슬롯, trigger='click' (기본은 'hover')
                  click 모드는 모바일/터치 환경 호환성↑ */}
              <Popover
                title={t('LBL_DEMO_POPOVER_TITLE', '팝오버 제목')}
                content={
                  <div style={{ width: 200 }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                      {t('LBL_DEMO_POPOVER_BODY', '클릭 시 표시되는 팝오버입니다.')}
                    </p>
                  </div>
                }
                trigger="click"
              >
                <Button size="small" icon={<InfoCircleOutlined />}>
                  Popover
                </Button>
              </Popover>
              {/* Tooltip 4-방향 시연 — placement: top/bottom/left/right
                  hover 시 자동 표시 (Popover 와 달리 trigger 명시 불필요) */}
              <Tooltip title={t('LBL_DEMO_TOOLTIP_TOP', '위쪽 툴팁')} placement="top">
                <Button size="small">{t('LBL_TOP', '위')}</Button>
              </Tooltip>
              <Tooltip title={t('LBL_DEMO_TOOLTIP_BOTTOM', '아래쪽 툴팁')} placement="bottom">
                <Button size="small">{t('LBL_BOTTOM', '아래')}</Button>
              </Tooltip>
              <Tooltip title={t('LBL_DEMO_TOOLTIP_LEFT', '왼쪽 툴팁')} placement="left">
                <Button size="small">{t('LBL_LEFT', '왼쪽')}</Button>
              </Tooltip>
              <Tooltip title={t('LBL_DEMO_TOOLTIP_RIGHT', '오른쪽 툴팁')} placement="right">
                <Button size="small">{t('LBL_RIGHT', '오른쪽')}</Button>
              </Tooltip>
            </Space>
            {/* ConfigProvider locale 토글 데모
                토글 버튼: 현재 선택된 locale 은 primary 강조, 비활성은 default
                ConfigProvider 자식의 DatePicker 가 즉시 ko↔en 전환됨 (월 이름, 요일 등) */}
            <Space wrap size={6} style={{ alignItems: 'center' }}>
              <Button
                size="small"
                type={cpLocale === 'ko' ? 'primary' : 'default'}
                onClick={() => setCpLocale('ko')}
              >
                한국어
              </Button>
              <Button
                size="small"
                type={cpLocale === 'en' ? 'primary' : 'default'}
                onClick={() => setCpLocale('en')}
              >
                English
              </Button>
              {/* 부분 영역만 다른 locale 적용 — 앱 전체 locale 은 main.tsx 의 최상위 ConfigProvider */}
              <ConfigProvider locale={cpLocale === 'ko' ? koKR : enUS}>
                <DatePicker size="small" />
              </ConfigProvider>
            </Space>
          </Space>
        </Card>
      </div>
    </PageContainer>
  )
}
